import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Send, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

/**
 * QENVA · Comments thread for an alert.
 *
 * Drop-in inside AlertDrawer. Loads comments on mount, posts new ones,
 * supports edit + soft-delete (author or admin via backend rules).
 */
function Avatar({ name, email, size = 26 }) {
  const initials = (name || email || "?").trim()[0].toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-qenva-blue/15 text-qenva-blue font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
    >
      {initials}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.round(diff / 86400)} j`;
    return d.toLocaleDateString();
  } catch (_) {
    return iso.slice(0, 10);
  }
}

export default function CommentsThread({
  alertId,
  currentUserId,
  testId = "comments-thread",
}) {
  const { t } = useTranslation("alerts");
  const [comments, setComments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");

  useEffect(() => {
    if (!alertId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/alm/alerts/${alertId}/comments`).catch(() => ({ data: { items: [] } })),
      api.get("/alm/members").catch(() => ({ data: { items: [] } })),
    ]).then(([cr, mr]) => {
      if (cancelled) return;
      setComments(cr.data?.items || []);
      setMembers(mr.data?.items || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [alertId]);

  const memberById = {};
  for (const m of members) memberById[m.user_id] = m;

  async function handleSubmit() {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    try {
      const res = await api.post(`/alm/alerts/${alertId}/comments`, { body });
      setComments((c) => [...c, res.data]);
      setDraft("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to comment");
    } finally {
      setPosting(false);
    }
  }

  async function handleDelete(commentId) {
    if (!confirm(t("comments.confirm_delete"))) return;
    try {
      await api.delete(`/alm/alerts/${alertId}/comments/${commentId}`);
      setComments((c) => c.filter((x) => x.id !== commentId));
      toast.success(t("comments.deleted_toast"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  }

  async function handleEditSubmit(commentId) {
    const body = editDraft.trim();
    if (!body) {
      setEditingId(null);
      return;
    }
    try {
      const res = await api.patch(`/alm/alerts/${alertId}/comments/${commentId}`, { body });
      setComments((c) => c.map((x) => (x.id === commentId ? res.data : x)));
      setEditingId(null);
      setEditDraft("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to edit");
    }
  }

  return (
    <section data-testid={testId}>
      <header className="flex items-center gap-2 mb-3">
        <MessageCircle size={14} className="text-qenva-blue" />
        <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-qenva-blue">
          {t("comments.title")}
        </p>
        <span className="text-[11.5px] text-zinc-400 tabular-nums">({comments.length})</span>
      </header>

      {loading ? (
        <p className="text-[12px] text-zinc-400" data-testid={`${testId}-loading`}>{t("common.loading")}</p>
      ) : comments.length === 0 ? (
        <p className="text-[12px] text-zinc-400 italic" data-testid={`${testId}-empty`}>{t("comments.empty")}</p>
      ) : (
        <ul className="space-y-3" data-testid={`${testId}-list`}>
          {comments.map((c) => {
            const author = memberById[c.author_user_id] || { name: "Utilisateur supprimé", email: "" };
            const isAuthor = currentUserId === c.author_user_id;
            const isEditing = editingId === c.id;
            return (
              <li key={c.id} className="flex gap-2.5" data-testid={`${testId}-item-${c.id}`}>
                <Avatar name={author.name} email={author.email} size={28} />
                <div className="flex-1 min-w-0">
                  <header className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-qenva-navy text-[12.5px] truncate">
                      {author.name || author.email}
                    </span>
                    <span className="text-[10.5px] text-zinc-400">
                      {relativeTime(c.created_at)}{c.edited && ` · ${t("comments.edited")}`}
                    </span>
                    {isAuthor && !isEditing && (
                      <span className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => { setEditingId(c.id); setEditDraft(c.body); }}
                          data-testid={`${testId}-edit-${c.id}`}
                          className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-qenva-blue"
                        >
                          <Pencil size={11} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(c.id)}
                          data-testid={`${testId}-delete-${c.id}`}
                          className="p-1 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                        >
                          <Trash2 size={11} />
                        </button>
                      </span>
                    )}
                  </header>
                  {isEditing ? (
                    <div className="space-y-1.5">
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={3}
                        data-testid={`${testId}-edit-input-${c.id}`}
                        className="w-full px-2.5 py-2 text-[12.5px] border border-zinc-200 rounded-lg focus:outline-none focus:border-qenva-blue/60 focus:ring-2 focus:ring-qenva-blue/15 resize-y"
                      />
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          type="button"
                          onClick={() => { setEditingId(null); setEditDraft(""); }}
                          className="inline-flex items-center gap-0.5 px-2 py-1 rounded text-[11px] font-semibold text-zinc-600 hover:bg-zinc-100"
                        >
                          <X size={11} /> {t("common.cancel")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditSubmit(c.id)}
                          data-testid={`${testId}-edit-save-${c.id}`}
                          className="inline-flex items-center gap-0.5 px-2 py-1 rounded text-[11px] font-semibold text-white bg-qenva-blue hover:bg-qenva-blue/90"
                        >
                          <Check size={11} /> {t("common.save")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p
                      className="text-[12.5px] text-zinc-900 whitespace-pre-wrap leading-relaxed bg-zinc-50 rounded-lg px-3 py-2 border border-zinc-200/60"
                      data-testid={`${testId}-body-${c.id}`}
                    >
                      {c.body}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 flex items-start gap-2" data-testid={`${testId}-composer`}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={t("comments.composer_placeholder")}
          rows={2}
          data-testid={`${testId}-input`}
          className="flex-1 px-3 py-2 text-[12.5px] border border-zinc-200 rounded-lg focus:outline-none focus:border-qenva-blue/60 focus:ring-2 focus:ring-qenva-blue/15 resize-y"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!draft.trim() || posting}
          data-testid={`${testId}-submit`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold bg-qenva-blue text-white hover:bg-qenva-blue/90 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Send size={12} /> {posting ? t("comments.sending") : t("comments.send")}
        </button>
      </div>
    </section>
  );
}
